import React, { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { APP_HOME } from "./paths";

import NotFound from "../pages/NotFound";
import Welcome from "../pages/LandingPage/Welcome";
import QuickReviewPage from "../pages/LandingPage/Review";
import UseCases from "../pages/LandingPage/UseCases";
import UseCaseDetail from "../pages/LandingPage/UseCases/UseCaseDetail";
import Pricing from "../pages/Pricing";
import Authentication from "../pages/Login";
import Team from "../pages/LandingPage/Team";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import ChangePassword from "../pages/changePassword";
import SignUp from "../pages/Signup";
import Verification from "../pages/verification";

import CompanyDashboard from "../pages/CompanyDashboard";
import InvitedUserOnboarding from "../pages/InvitedUserOnboarding";
import SubscriptionManagement from "../pages/SubscriptionManagement";

import SharedVideo from "../pages/SharedVideo";
import QuickReview from "../pages/QuickReview";

import QuickReviewHome from "../pages/QuickReview/Home";
import ReviewCollectionDetail from "../pages/QuickReview/ReviewCollectionDetail";

const ScrollToTop = ({ children }: { children: React.ReactNode }) => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return <>{children}</>;
};

// Collection IDs are shared, so old search links open the same collection in QuickReview.
const CollectionRedirect: React.FC = () => {
    const { id } = useParams();
    return <Navigate to={`/quick-review/collections/${id}`} replace />;
};

const AppRoutes: React.FC = () => {
    return (
        <ScrollToTop>
            <Routes>
                <Route path="/" element={<Welcome />} />
                <Route path="/quick-review" element={<QuickReviewPage />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/team" element={<Team />} />
                <Route path="/login" element={<Authentication />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/verification" element={<Verification />} />
                <Route path="/change-password" element={<ChangePassword />} />

                {/* Retired search product */}
                <Route path="/collections" element={<Navigate to={APP_HOME} replace />} />
                <Route path="/collections/:id" element={<CollectionRedirect />} />
                <Route path="/collections/:id/search" element={<Navigate to={APP_HOME} replace />} />
                <Route path="/quick-search" element={<Navigate to="/" replace />} />
                <Route path="/quick-gen" element={<Navigate to="/" replace />} />
                <Route path="/about" element={<Navigate to="/" replace />} />

                <Route
                    path="/company"
                    element={
                        <ProtectedRoute>
                            <CompanyDashboard />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/subscription"
                    element={
                        <ProtectedRoute>
                            <SubscriptionManagement />
                        </ProtectedRoute>
                    }
                />

                <Route path="/onboarding" element={<InvitedUserOnboarding />} />
                <Route path="/shared/:shareToken" element={<SharedVideo />} />
                <Route
                    path="/quick-review/:userId/:videoId"
                    element={
                        <ProtectedRoute>
                            <QuickReview />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={APP_HOME}
                    element={
                        <ProtectedRoute>
                            <QuickReviewHome />
                        </ProtectedRoute>
                    }
                />
                <Route path="/quick-review/home" element={<Navigate to={APP_HOME} replace />} />
                <Route
                    path="/quick-review/collections/:collectionId"
                    element={
                        <ProtectedRoute>
                            <ReviewCollectionDetail />
                        </ProtectedRoute>
                    }
                />

                <Route path="/use-cases" element={<UseCases />} />
                <Route path="/use-cases/:id" element={<UseCaseDetail />} />

                <Route path="*" element={<NotFound />} />
            </Routes>
        </ScrollToTop>
    );
};

export default AppRoutes;
